<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card p-fluid shared-enquiry-settings">
        <h4>Shared Enquiry Settings</h4>
        <p class="hint">The New Enquiry form's mandatory fields and how an enquiry's value is worked out. Admin only.</p>

        <div v-if="loading" class="p-text-center"><ProgressSpinner /></div>

        <div v-else-if="loadError" id="loadError" class="p-invalid">{{ loadError }}</div>

        <div v-else>
          <h5>Mandatory fields</h5>
          <p class="hint">Ticked fields must be filled in before a new enquiry, or a customer added to one, can be saved.</p>
          <div v-for="g in groups" :key="g.key" class="p-field field-group" :id="'group-' + g.key">
            <label class="group-header">{{ g.header }}</label>
            <div v-if="!g.fields.length" class="hint">None.</div>
            <div v-for="f in g.fields" :key="f.fieldKey" class="p-field-checkbox">
              <Checkbox :id="'mandatory-' + f.fieldKey" v-model="f.mandatory" :binary="true" />
              <label :for="'mandatory-' + f.fieldKey">{{ f.label }}</label>
            </div>
          </div>

          <h5 class="p-mt-4">Enquiry value</h5>
          <p class="hint">The value of an enquiry with several customers' quotations.</p>
          <div class="p-field">
            <SelectButton id="valueBasis" :value="basis" :options="bases" optionLabel="label" optionValue="value"
              @input="pickBasis" />
          </div>

          <h5 class="p-mt-4">Enquiry form layout</h5>
          <p class="hint">How the New Enquiry form is laid out for everyone: one page, or in tabs.</p>
          <div class="p-field">
            <SelectButton id="formLayout" :value="layout" :options="layouts" optionLabel="label" optionValue="value"
              @input="pickLayout" />
          </div>

          <p id="noExecMoved" class="hint p-mt-4">
            The no-sales-exec warnings are switched on and off in SETTINGS &gt; Warnings &amp; Approvals &gt; Settings, tab "No sales exec".
          </p>

          <div class="p-d-flex p-jc-end p-mt-3">
            <Button label="Reset" icon="pi pi-refresh" class="p-button-text p-mr-2" style="width: auto" @click="reset" />
            <Button id="saveSharedEnquirySettings" label="Save" icon="pi pi-check" style="width: auto"
              :disabled="saving" @click="save" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from './sharedEnquiryApi'
import { errorText } from './sharedEnquiryLogic'
import { VALUE_BASES, fieldGroups, toForm, changedFields } from './sharedEnquirySettingsForm'
import { LAYOUTS, ONE_PAGE, isLayout, forgetLayout } from './enquiryLayout'

export default {
  name: 'SharedEnquirySettings',
  data: () => ({
    savedFields: [],
    savedBasis: null,
    form: [],
    basis: null,
    bases: VALUE_BASES,
    loading: true,
    loadError: '',
    layouts: LAYOUTS,
    layout: ONE_PAGE,
    savedLayout: ONE_PAGE,
    saving: false
  }),
  computed: {
    groups() {
      return fieldGroups(this.form)
    }
  },
  created() {
    this.load()
  },
  methods: {
    // PrimeVue 2 SelectButton emits null when the picked option is clicked again: keep the pick
    pickBasis(v) {
      if (v) this.basis = v
    },
    pickLayout(v) {
      if (v) this.layout = v
    },
    applyFields(rows) {
      this.savedFields = rows || []
      this.form = toForm(this.savedFields)
    },
    load() {
      this.loading = true
      // the layout is read on its own: if its endpoint is not there yet, the other settings still load (One page)
      api.formLayout()
        .then(r => { this.savedLayout = r && isLayout(r.layout) ? r.layout : ONE_PAGE; this.layout = this.savedLayout })
        .catch(() => {})
      return Promise.all([api.fieldSettings(), api.valueBasis()])
        .then(([rows, basis]) => {
          this.applyFields(rows)
          this.savedBasis = basis && basis.basis
          this.basis = this.savedBasis
          this.loadError = ''
        })
        .catch(e => {
          this.loadError = errorText(e, 'The shared enquiry settings could not be loaded.')
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: this.loadError, life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    reset() {
      this.form = toForm(this.savedFields)
      this.basis = this.savedBasis
      this.layout = this.savedLayout
    },
    async save() {
      const fields = changedFields(this.form, this.savedFields)
      const basisChanged = !!this.basis && this.basis !== this.savedBasis
      const layoutChanged = isLayout(this.layout) && this.layout !== this.savedLayout
      if (!fields.length && !basisChanged && !layoutChanged) {
        this.$toast.add({ severity: 'info', summary: 'Nothing to save', detail: 'No setting was changed.', life: 3000 })
        return
      }
      this.saving = true
      try {
        // one PUT each; a refused second one leaves the first saved, and the form shows what is saved
        if (fields.length) this.applyFields(await api.saveFieldSettings(fields))
        if (basisChanged) {
          const answer = await api.saveValueBasis(this.basis)
          this.savedBasis = answer && answer.basis
          this.basis = this.savedBasis
        }
        if (layoutChanged) {
          const answer = await api.saveFormLayout(this.layout)
          this.savedLayout = answer && isLayout(answer.layout) ? answer.layout : this.layout
          this.layout = this.savedLayout
          forgetLayout()
        }
        this.$toast.add({ severity: 'success', summary: 'Settings saved', detail: 'The shared enquiry settings were saved.', life: 5000 })
      } catch (e) {
        this.$toast.add({ severity: 'error', summary: 'Not saved', detail: errorText(e, 'The shared enquiry settings could not be saved.'), life: 5000 })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
/* the card is full width like every other page; only the form fields keep a readable width */
.shared-enquiry-settings .p-field, .shared-enquiry-settings .p-formgrid, .shared-enquiry-settings .p-field-checkbox { max-width: 760px; }
.shared-enquiry-settings .hint { display: block; color: #6c757d; margin-bottom: 0.5rem; }
.shared-enquiry-settings .group-header { font-weight: 600; }
.shared-enquiry-settings .field-group { margin-bottom: 1rem; }
</style>
